// Constants
import { Endpoints } from "~constants/endpoints"
import { NotificationType, NotificationPayload } from "~constants/notifications"

// Utils
import { Api } from "~utils/api"
import { urlQueryBuilder } from "~utils/utils"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import checkAuthorization from "~rbac/utils/checkAuthorization"

// Shapes
import { PostUserShape } from "~shapes/user"
import { TableOptionsShape } from "~shapes/api-table"
import { STATUS } from "~constants/status"

interface UpdateUserPayload extends Omit<PostUserShape, "roles"> {
  roles?: string[]
}

// Gateway response: { page: { number, size, totalElements, totalPages }, _embedded: { users: [] }, _links }
const getUsersFromResponse = (data: any) => data?._embedded?.users ?? []

const normalizeListResponse = (data: any) => ({
  page: data.page ?? {
    number: 0,
    size: 0,
    totalPages: 0,
    totalElements: 0,
  },
  _embedded: { users: getUsersFromResponse(data) },
  _links: data._links ?? {},
})

const UserService = {
  getUsers: async (params?: TableOptionsShape & { id?: string }) => {
    const base = Endpoints.GATEWAY_USERS_V1.URL
    if (params?.id) {
      const { data } = await Api.get(`${base}/${params.id}`)
      return data
    }
    const options = { ...params, status: STATUS.ACTIVE }
    if (options.email) options.email = encodeURIComponent(options.email)
    const query = urlQueryBuilder(options)
    const { data } = await Api.get(`${base}?${query}`)
    return normalizeListResponse(data)
  },
  getUserById: async (id: string, extraInfo = true) => {
    let userUrl = `${Endpoints.USERS.URL}/${id}`

    const { data: user } = await Api.get(userUrl)

    if (extraInfo) {
      const canGetRoles = checkAuthorization(RBAC_RULES.UPDATE_USER_ROLES)
      const canGetCompany = checkAuthorization(RBAC_RULES.UPDATE_USER_COMPANY)

      try {
        if (canGetRoles) {
          const userRolesPath = `${Endpoints.USERS.URL}/${id}/roles`
          const { data: roles } = await Api.get(userRolesPath)
          user.roles = roles._embedded.roles
        }
      } catch (err) {}

      try {
        if (canGetCompany) {
          const userCompanyPath = `${Endpoints.USERS.URL}/${id}/company`
          const { data: company } = await Api.get(userCompanyPath)
          user.company = company
        }
      } catch (err) {}
    }

    return user
  },
  postUser: async (user: PostUserShape) => {
    const { data } = await Api.post(Endpoints.GATEWAY_USERS_V2.URL, user)
    return data
  },
  patchUser: async (user: Partial<PostUserShape> | UpdateUserPayload) => {
    const { id, password, ...rest } = user
    const payload = {
      ...rest,
      ...(password && password !== "" && { password }),
    }
    const { data } = await Api.patch(
      `${Endpoints.GATEWAY_USERS_V1.URL}/${id}`,
      payload,
    )
    return data
  },
  DELETEUser: async (userId: string) => {
    const { data } = await Api.patch(
      `${Endpoints.GATEWAY_USERS_V1.URL}/${userId}`,
      {
        status: "Inactive",
      },
    )
    return data
  },
  patchNewPassword: async (payload: { password: string }, userId: string) => {
    const url = `${Endpoints.USERS.URL}/${userId}`
    const { data } = await Api.patch(url, payload)
    return data
  },
  getUserByEmail: async (email: string) => {
    const encodedEmail = encodeURIComponent(email)
    let userUrl = `${Endpoints.USERS.URL}?email=${encodedEmail}`

    try {
      const { data: user } = await Api.get(userUrl)
      return user
    } catch (error) {
      return Promise.reject(error)
    }
  },
  sendNotification: async (userId: string, type: NotificationType) => {
    const payload: NotificationPayload = {
      delivery_type: "EMAIL",
      recipients: {
        user_ids: [userId],
        broadcast: false,
      },
      type,
      data: {},
    }
    const { data } = await Api.post(
      Endpoints.GATEWAY_EVENT_NOTIFICATIONS.URL,
      payload,
    )
    return data
  },
}

export default UserService
